import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Table2,
  KanbanSquare,
  Zap,
  Users as UsersIcon,
  FolderKanban,
  Target,
  Filter,
  Palette,
  Columns3,
  Tags,
  Webhook,
  BarChart3,
  LayoutTemplate,
  Building2,
  Search,
  Star,
  Share2,
  Trash2,
  Eye,
  Check,
} from 'lucide-react';

// Modern components imports
import ModernProjectTable from './projects/ModernProjectTable';
import { setBoardIdentities } from './projects/avatarUtils';
import ModernBoardMembers from './projects/ModernBoardMembers';
import ModernCreateBoardModal from './projects/ModernCreateBoardModal';
import ProjectWorkload from './projects/ProjectWorkload';

// Existing components 
import ProjectKanban from './projects/ProjectKanban';
import ProjectTimeline from './projects/ProjectTimeline';
import ProjectCalendar from './projects/ProjectCalendar';
import ProjectDashboard from './projects/ProjectDashboard';
import ProjectTags from './projects/ProjectTags';
import ProjectAutomations from './projects/ProjectAutomations';
import ProjectNotifications from './projects/ProjectNotifications';
import ProjectColumns from './projects/ProjectColumns';
import ProjectGroupCustomization from './projects/ProjectGroupCustomization';
import ProjectIntegrations from './projects/ProjectIntegrations';
import ProjectOptimization from './projects/ProjectOptimization';
import ProjectAnalytics from './projects/ProjectAnalytics';
import NotificationBell from './projects/NotificationBell';
import BoardTemplates from './projects/BoardTemplates';
import ProjectFilters from './projects/ProjectFilters';
import BoardWebhooks from './projects/BoardWebhooks';
import PortfolioView from './projects/PortfolioView';
import GoalsOKR from './projects/GoalsOKR';

export default function ModernProjects({ api, user }) {
  const [boards, setBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [currentView, setCurrentView] = useState('table');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showGroupCustomization, setShowGroupCustomization] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showOptimization, setShowOptimization] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [projectDarkMode, setProjectDarkMode] = useState(() => localStorage.getItem('nexo_project_theme') === 'dark');

  useEffect(() => {
    localStorage.setItem('nexo_project_theme', projectDarkMode ? 'dark' : 'light');
  }, [projectDarkMode]);

  // Board data states
  const [boardGroups, setBoardGroups] = useState([]);
  const [boardItems, setBoardItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [boardMembers, setBoardMembers] = useState([]);
  setBoardIdentities(boardMembers); // registra identificações/iniciais únicas antes de renderizar os avatares
  const [customColumns, setCustomColumns] = useState([]);
  const [boardListSummary, setBoardListSummary] = useState(null);
  const [boardInsights, setBoardInsights] = useState(null);
  const [filteredInsights, setFilteredInsights] = useState(null);
  const [activeAdvancedFilter, setActiveAdvancedFilter] = useState(null);
  const [onlyMyTasks, setOnlyMyTasks] = useState(false);
  const [boardDataRefreshing, setBoardDataRefreshing] = useState(false);

  // Workspace states
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [showMoveWorkspace, setShowMoveWorkspace] = useState(false);
  const [moveToWorkspaceId, setMoveToWorkspaceId] = useState('');
  const [showWorkspaceForm, setShowWorkspaceForm] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [workspaceExpanded, setWorkspaceExpanded] = useState({});

  // Admin view
  const isAdmin = user?.permissions?.includes('manage_system') || user?.role === 'admin';
  const [adminViewActive, setAdminViewActive] = useState(false);

  // Board name inline editing (header)
  const [editingBoardName, setEditingBoardName] = useState(false);
  const [boardNameInput, setBoardNameInput] = useState('');

  // Board rename via dropdown pencil
  const [editingBoardId, setEditingBoardId] = useState(null);
  const [editingBoardNameInput, setEditingBoardNameInput] = useState('');

  // Custom board dropdown (replaces Radix Select to allow inline rename)
  const [boardDropdownOpen, setBoardDropdownOpen] = useState(false);
  const [focusedBoardIdx, setFocusedBoardIdx] = useState(-1);
  const boardDropdownRef = useRef(null);
  const boardTriggerRef = useRef(null);
  const flatBoardsRef = useRef([]);
  const focusedBoardIdxRef = useRef(-1);

  // Refs for workspace form dropdown
  const workspaceFormRef = useRef(null);
  const workspaceFormTriggerRef = useRef(null);
  const [focusedWsIdx, setFocusedWsIdx] = useState(-1);
  const focusedWsIdxRef = useRef(-1);

  // Refs for move-to-workspace dropdown
  const moveWorkspaceRef = useRef(null);
  const moveWorkspaceTriggerRef = useRef(null);

  // Ref to track current adminViewActive for use inside polling interval (avoids stale closure)
  const adminViewActiveRef = useRef(adminViewActive);
  // Ref to track current selectedBoard for use inside polling interval (avoids stale closure)
  const selectedBoardRef = useRef(null);

  // Drag-and-drop board reorder state
  const dragBoardRef = useRef(null);
  const dragBoardWorkspaceRef = useRef(null);
  const boardItemRefsMap = useRef({});
  const [dragOverBoardId, setDragOverBoardId] = useState(null);
  const [itemDropBoardId, setItemDropBoardId] = useState(null); // quadro destacado ao arrastar uma tarefa sobre ele
  const [dragOverWorkspaceId, setDragOverWorkspaceId] = useState(null);
  const [dragOverPosition, setDragOverPosition] = useState(null); // 'before' | 'after'
  const dragOverPositionRef = useRef(null);
  const dragOverBoardIdRef = useRef(null);
  const dragBoardHeightRef = useRef(32);
  const [exitingDrop, setExitingDrop] = useState(null); // { boardId, position, dragging }
  const exitingDropTimerRef = useRef(null);

  // Board delete via dropdown trash
  const [deletingBoardId, setDeletingBoardId] = useState(null);
  const [deletingBoardName, setDeletingBoardName] = useState('');

  // Phase 2: Search, Favorites, Share
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  // Keep adminViewActiveRef in sync so polling interval always reads the current value
  useEffect(() => {
    adminViewActiveRef.current = adminViewActive;
  }, [adminViewActive]);

  // Keep selectedBoardRef in sync so polling interval always reads the current board
  useEffect(() => {
    selectedBoardRef.current = selectedBoard;
  }, [selectedBoard]);

  useEffect(() => {
    fetchBoards();
    fetchWorkspaces();

    const lastActivityRef = { current: Date.now() };
    const ACTIVE_INTERVAL_MS = 10000;  // 10s when user is active
    const IDLE_THRESHOLD_MS = 60000;   // consider idle after 60s without interaction

    const onActivity = () => { lastActivityRef.current = Date.now(); };
    document.addEventListener('mousemove', onActivity, { passive: true });
    document.addEventListener('keydown', onActivity, { passive: true });
    document.addEventListener('click', onActivity, { passive: true });

    const pollingInterval = setInterval(async () => {
      if (document.hidden) return;                                        // tab in background — skip
      if (Date.now() - lastActivityRef.current > IDLE_THRESHOLD_MS) return; // user idle — skip

      // Sync board list
      fetchBoards(adminViewActiveRef.current, true);

      // Sync open board data (groups, items, members, columns) — silent, no toast
      const board = selectedBoardRef.current;
      if (board) {
        try {
          const [groupsRes, itemsRes, membersRes, columnsRes, summaryRes, dashboardRes] = await Promise.all([
            api.get(`/projects/boards/${board.id}/groups`),
            api.get(buildBoardItemsUrl(board.id, onlyMyTasks)),
            api.get(`/projects/boards/${board.id}/members`),
            api.get(`/projects/boards/${board.id}/columns`).catch(() => ({ data: [] })),
            api.get(buildBoardListSummaryUrl(board.id, onlyMyTasks)).catch(() => ({ data: null })),
            api.get(`/projects/boards/${board.id}/dashboard`).catch(() => ({ data: null }))
          ]);
          setBoardGroups(dedupById(groupsRes.data));
          const uniqueItems = dedupById(itemsRes.data);
          setBoardItems(uniqueItems);
          setBoardMembers(dedupById(membersRes.data));
          setCustomColumns(columnsRes.data);
          setBoardListSummary(summaryRes.data);
          setBoardInsights(dashboardRes.data);
        } catch {} // silent — don't disturb the user
      }
    }, ACTIVE_INTERVAL_MS);

    return () => {
      clearInterval(pollingInterval);
      document.removeEventListener('mousemove', onActivity);
      document.removeEventListener('keydown', onActivity);
      document.removeEventListener('click', onActivity);
    };
  }, [onlyMyTasks]);


  useEffect(() => {
    if (selectedBoard) {
      fetchBoardData();
    }
  }, [selectedBoard, onlyMyTasks]);

  useEffect(() => {
    if (!activeAdvancedFilter?.hasRules) {
      setFilteredItems(boardItems);
      setFilteredInsights(null);
      return;
    }

    const nextItems = boardItems.filter(item => (activeAdvancedFilter.matchedParentIds || []).includes(item.id));
    setFilteredItems(nextItems);
    setFilteredInsights(
      buildFilteredInsights(
        boardInsights,
        activeAdvancedFilter.matchedParentIds,
        activeAdvancedFilter.matchedSubtaskIds
      )
    );
  }, [activeAdvancedFilter, boardInsights, boardItems]);

  useEffect(() => {
    setActiveAdvancedFilter(null);
  }, [selectedBoard?.id]);

  useEffect(() => {
    if (focusedBoardIdx < 0) return;
    const focused = flatBoardsRef.current[focusedBoardIdx];
    if (!focused) return;
    const el = boardItemRefsMap.current[focused.id];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [focusedBoardIdx]);

  useEffect(() => {
    if (!boardDropdownOpen) {
      setFocusedBoardIdx(-1);
      focusedBoardIdxRef.current = -1;
      return;
    }
    const handleOutsideClick = (e) => {
      if (boardDropdownRef.current && !boardDropdownRef.current.contains(e.target)) {
        setBoardDropdownOpen(false);
        setEditingBoardId(null);
      }
    };
    const handleKeyDown = (e) => {
      const isInInput = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable;
      if (e.key === 'Escape') {
        // Escape always closes, even from inputs
        setBoardDropdownOpen(false);
        setEditingBoardId(null);
        boardTriggerRef.current?.focus();
      } else if (!isInInput && e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedBoardIdx(i => {
          const next = Math.min(i + 1, flatBoardsRef.current.length - 1);
          focusedBoardIdxRef.current = next;
          return next;
        });
      } else if (!isInInput && e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedBoardIdx(i => {
          const next = Math.max(i - 1, 0);
          focusedBoardIdxRef.current = next;
          return next;
        });
      } else if (!isInInput && e.key === 'Enter') {
        const focused = flatBoardsRef.current[focusedBoardIdxRef.current];
        if (focused) {
          e.preventDefault();
          setSelectedBoard(focused);
          setBoardDropdownOpen(false);
          setFocusedBoardIdx(-1);
          focusedBoardIdxRef.current = -1;
        }
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [boardDropdownOpen]);

  useEffect(() => {
    if (!showWorkspaceForm) {
      setFocusedWsIdx(-1);
      focusedWsIdxRef.current = -1;
      return;
    }
    const handleOutsideClick = (e) => {
      if (workspaceFormRef.current && !workspaceFormRef.current.contains(e.target)) {
        setShowWorkspaceForm(false);
      }
    };
    const handleKeyDown = (e) => {
      const isInInput = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable;
      if (e.key === 'Escape') {
        setShowWorkspaceForm(false);
        workspaceFormTriggerRef.current?.focus();
      } else if (!isInInput && e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedWsIdx(i => {
          const next = Math.min(i + 1, workspaces.length - 1);
          focusedWsIdxRef.current = next;
          return next;
        });
      } else if (!isInInput && e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedWsIdx(i => {
          const next = Math.max(i - 1, 0);
          focusedWsIdxRef.current = next;
          return next;
        });
      } else if (!isInInput && e.key === 'Enter') {
        const focused = workspaces[focusedWsIdxRef.current];
        if (focused) {
          e.preventDefault();
          setSelectedWorkspace(focused);
          setShowWorkspaceForm(false);
          setFocusedWsIdx(-1);
          focusedWsIdxRef.current = -1;
        }
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showWorkspaceForm, workspaces]);

  useEffect(() => {
    if (!showMoveWorkspace) return;
    const handleOutsideClick = (e) => {
      if (moveWorkspaceRef.current && !moveWorkspaceRef.current.contains(e.target)) {
        setShowMoveWorkspace(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowMoveWorkspace(false);
        moveWorkspaceTriggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showMoveWorkspace]);

  const fetchWorkspaces = async () => {
    try {
      const res = await api.get('/projects/workspaces');
      setWorkspaces(res.data);
      if (res.data.length > 0) {
        const init = {};
        res.data.forEach(w => { init[w.id] = true; });
        setWorkspaceExpanded(init);
      }
    } catch (e) {
      // workspaces optional — ignore
    }
  };

  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName.trim()) return;
    try {
      const res = await api.post('/projects/workspaces', { name: newWorkspaceName.trim() });
      setWorkspaces(prev => [...prev, res.data]);
      setWorkspaceExpanded(prev => ({ ...prev, [res.data.id]: true }));
      setNewWorkspaceName('');
      setShowWorkspaceForm(false);
      toast.success('Workspace criado!');
    } catch (e) {
      toast.error('Erro ao criar workspace');
    }
  };

  const handleDeleteWorkspace = async (ws) => {
    if (!window.confirm(`Excluir workspace "${ws.name}"?`)) return;
    try {
      await api.delete(`/projects/workspaces/${ws.id}`);
      setWorkspaces(prev => prev.filter(w => w.id !== ws.id));
      if (selectedWorkspace?.id === ws.id) setSelectedWorkspace(null);
      toast.success('Workspace excluído');
    } catch (e) {
      toast.error('Erro ao excluir workspace');
    }
  };

  const dedupById = (arr) => {
    const seen = new Set();
    return (arr || []).filter(x => { if (seen.has(x.id)) return false; seen.add(x.id); return true; });
  };

  const mergeItemDraft = (currentItem, updatedItem) => ({
    ...currentItem,
    ...updatedItem,
    column_values: {
      ...(currentItem?.column_values || {}),
      ...(updatedItem?.column_values || {})
    },
    rollup: currentItem?.rollup,
  });

  const patchBoardInsightsWithItem = (currentInsights, updatedItem) => {
    if (!currentInsights || !updatedItem?.id) return currentInsights;

    const nextGroups = (currentInsights.groups || []).map(group => ({
      ...group,
      items: (group.items || []).map(item => (
        item.id === updatedItem.id ? mergeItemDraft(item, updatedItem) : item
      )),
    }));

    const nextWorkloadItems = (currentInsights.workload_items || []).map(item => (
      item.id === updatedItem.id
        ? {
            ...item,
            ...updatedItem,
            due_date: updatedItem.column_values?.due_date || item.due_date,
            priority_key: updatedItem.column_values?.priority || item.priority_key,
            priority_label: updatedItem.column_values?.priority || item.priority_label,
            is_done: updatedItem.column_values?.status_category
              ? updatedItem.column_values.status_category === 'done'
              : ['feito', 'done', 'concluido', 'concluído'].includes(String(updatedItem.column_values?.status || '').toLowerCase()),
          }
        : item
    ));

    return {
      ...currentInsights,
      groups: nextGroups,
      workload_items: nextWorkloadItems,
    };
  };

  const buildFilteredInsights = (sourceInsights, matchedParentIds = [], matchedSubtaskIds = []) => {
    if (!sourceInsights) return null;

    const parentIdSet = new Set(matchedParentIds || []);
    const subtaskIdSet = new Set(matchedSubtaskIds || []);

    const filteredGroups = (sourceInsights.groups || []).map(group => {
      const nextItems = (group.items || []).flatMap(item => {
        const parentMatched = parentIdSet.has(item.id);
        const allSubtasks = item.rollup?.subtasks || [];
        const matchedSubtasks = allSubtasks.filter(subtask => subtaskIdSet.has(subtask.id));
        if (!parentMatched && matchedSubtasks.length === 0) return [];
        if (parentMatched || allSubtasks.length === 0) return [item];

        const completedSubtasks = matchedSubtasks.filter(subtask => subtask.is_done).length;
        const blockedSubtasks = matchedSubtasks.filter(subtask => subtask.status_key === 'parado').length;
        const overdueSubtasks = matchedSubtasks.filter(subtask => subtask.is_overdue).length;
        const plannedMinutes = matchedSubtasks.reduce((total, subtask) => total + (subtask.planned_minutes || 0), 0);
        const trackedMinutes = matchedSubtasks.reduce((total, subtask) => total + (subtask.tracked_minutes || 0), 0);
        const assignees = [...new Map(
          matchedSubtasks
            .flatMap(subtask => subtask.assignees || [])
            .map(assignee => [`${assignee.id}::${assignee.name}`, assignee])
        ).values()];
        const openDueDates = matchedSubtasks
          .filter(subtask => subtask.due_date && !subtask.is_done)
          .map(subtask => new Date(`${String(subtask.due_date).slice(0, 10)}T12:00:00`));

        let statusKey = 'pendente';
        if (matchedSubtasks.length && completedSubtasks === matchedSubtasks.length) statusKey = 'feito';
        else if (matchedSubtasks.some(subtask => subtask.status_key === 'em_progresso') || completedSubtasks > 0) statusKey = 'em_progresso';
        else if (blockedSubtasks > 0) statusKey = 'parado';

        return [{
          ...item,
          rollup: {
            ...item.rollup,
            status_key: statusKey,
            status_label: statusKey === 'feito'
              ? 'Concluído'
              : statusKey === 'em_progresso'
                ? 'Em progresso'
                : statusKey === 'parado'
                  ? 'Bloqueado'
                  : item.rollup?.status_label || 'Sem status',
            is_done: matchedSubtasks.length > 0 && completedSubtasks === matchedSubtasks.length,
            is_overdue: overdueSubtasks > 0,
            planned_minutes: plannedMinutes,
            tracked_minutes: trackedMinutes,
            subtasks_total: matchedSubtasks.length,
            subtasks_completed: completedSubtasks,
            open_subtasks: matchedSubtasks.length - completedSubtasks,
            blocked_subtasks: blockedSubtasks,
            overdue_subtasks: overdueSubtasks,
            completion_percentage: matchedSubtasks.length ? Math.round((completedSubtasks / matchedSubtasks.length) * 1000) / 10 : 0,
            assignees,
            next_due_date: openDueDates.length ? new Date(Math.min(...openDueDates.map(date => date.getTime()))).toISOString().slice(0, 10) : null,
            latest_due_date: openDueDates.length ? new Date(Math.max(...openDueDates.map(date => date.getTime()))).toISOString().slice(0, 10) : null,
            subtasks: matchedSubtasks,
          },
        }];
      });

      const totalItems = nextItems.reduce((total, item) => total + 1 + (item.rollup?.subtasks_total || 0), 0);
      const completedItems = nextItems.reduce((total, item) => total + (item.rollup?.is_done ? 1 : 0) + (item.rollup?.subtasks_completed || 0), 0);
      const overdueItems = nextItems.reduce((total, item) => total + (item.rollup?.is_overdue ? 1 : 0) + (item.rollup?.overdue_subtasks || 0), 0);
      const subtasks = nextItems.reduce((total, item) => total + (item.rollup?.subtasks_total || 0), 0);

      return {
        ...group,
        items: nextItems,
        total_items: totalItems,
        primary_tasks: nextItems.length,
        subtasks,
        completed_items: completedItems,
        overdue_items: overdueItems,
        progress_percentage: totalItems ? Math.round((completedItems / totalItems) * 1000) / 10 : 0,
      };
    }).filter(group => (group.items || []).length > 0);

    const filteredWorkloadItems = (sourceInsights.workload_items || []).filter(item =>
      subtaskIdSet.has(item.id) ||
      parentIdSet.has(item.id) ||
      (item.parent_id && parentIdSet.has(item.parent_id))
    );

    const totalItems = filteredGroups.reduce((total, group) => total + (group.total_items || 0), 0);
    const completedItems = filteredGroups.reduce((total, group) => total + (group.completed_items || 0), 0);
    const overdueItems = filteredGroups.reduce((total, group) => total + (group.overdue_items || 0), 0);
    const primaryTasks = filteredGroups.reduce((total, group) => total + (group.primary_tasks || 0), 0);
    const subtasks = filteredGroups.reduce((total, group) => total + (group.subtasks || 0), 0);

    return {
      ...sourceInsights,
      groups: filteredGroups,
      group_progress: filteredGroups,
      workload_items: filteredWorkloadItems,
      summary: {
        ...(sourceInsights.summary || {}),
        total_items: totalItems,
        primary_tasks: primaryTasks,
        subtasks,
        completed_items: completedItems,
        open_items: Math.max(0, totalItems - completedItems),
        overdue_items: overdueItems,
        progress_percentage: totalItems ? Math.round((completedItems / totalItems) * 1000) / 10 : 0,
        planned_minutes: filteredWorkloadItems.reduce((total, item) => total + (item.planned_minutes || 0), 0),
        tracked_minutes: filteredWorkloadItems.reduce((total, item) => total + (item.tracked_minutes || 0), 0),
      },
    };
  };

  const buildBoardItemsUrl = (boardId, assignedOnly = onlyMyTasks) =>
    `/projects/boards/${boardId}/items${assignedOnly ? '?assigned_to_me=true' : ''}`;
  const buildBoardListSummaryUrl = (boardId, assignedOnly = onlyMyTasks) =>
    `/projects/boards/${boardId}/list-summary${assignedOnly ? '?assigned_to_me=true' : ''}`;

  const fetchBoards = async (forceAdminView, silent = false) => {
    try {
      const useAdmin = forceAdminView !== undefined ? forceAdminView : adminViewActive;
      const url = (isAdmin && useAdmin) ? '/projects/boards?all=true' : '/projects/boards';
      const response = await api.get(url);
      const unique = dedupById(response.data);
      setBoards(unique);
      
      // Auto-select first board if none selected — functional setter avoids stale closure
      setSelectedBoard(prev => {
        if (!prev && unique.length > 0) return unique[0];
        return prev;
      });
      return unique;
    } catch (error) {
      if (!silent) {
        console.error('Error fetching boards:', error);
        toast.error('Erro ao carregar quadros');
      }
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchBoardData = async (boardOverride = selectedBoard) => {
    if (!boardOverride) return;
    
    try {
      // Fetch groups, items, members, and custom columns in parallel
      const [groupsRes, itemsRes, membersRes, columnsRes, summaryRes, dashboardRes] = await Promise.all([
        api.get(`/projects/boards/${boardOverride.id}/groups`),
        api.get(buildBoardItemsUrl(boardOverride.id)),
        api.get(`/projects/boards/${boardOverride.id}/members`),
        api.get(`/projects/boards/${boardOverride.id}/columns`).catch(() => ({ data: [] })),
        api.get(buildBoardListSummaryUrl(boardOverride.id)).catch(() => ({ data: null })),
        api.get(`/projects/boards/${boardOverride.id}/dashboard`).catch(() => ({ data: null }))
      ]);

      setBoardGroups(dedupById(groupsRes.data));
      const uniqueItems = dedupById(itemsRes.data);
      setBoardItems(uniqueItems);
      setBoardMembers(dedupById(membersRes.data));
      setCustomColumns(columnsRes.data);
      setBoardListSummary(summaryRes.data);
      setBoardInsights(dashboardRes.data);
    } catch (error) {
      console.error('Error fetching board data:', error);
      toast.error('Erro ao carregar dados do quadro');
    }
  };

  const handleDashboardRefresh = async () => {
    if (!selectedBoard) return;

    const currentBoardId = selectedBoard.id;
    const refreshedBoards = await fetchBoards(undefined, true);
    const refreshedSelectedBoard = refreshedBoards.find(board => board.id === currentBoardId) || selectedBoard;

    if (refreshedSelectedBoard.id !== selectedBoard.id || refreshedSelectedBoard.name !== selectedBoard.name) {
      setSelectedBoard(refreshedSelectedBoard);
    }

    await fetchBoardData(refreshedSelectedBoard);
  };

  // Função específica para atualizar apenas as colunas
  const fetchCustomColumns = async () => {
    if (!selectedBoard) return;
    
    try {
      const columnsRes = await api.get(`/projects/boards/${selectedBoard.id}/columns`);
      setCustomColumns(columnsRes.data);
    } catch (error) {
      console.error('Error fetching custom columns:', error);
      // Don't show error toast for column fetch as it's not critical
    }
  };

  const handleItemPatched = (updatedItem) => {
    if (!updatedItem?.id) return;

    setBoardItems(previous => previous.map(item => (
      item.id === updatedItem.id ? mergeItemDraft(item, updatedItem) : item
    )));

    setBoardInsights(previous => patchBoardInsightsWithItem(previous, updatedItem));
    setFilteredInsights(previous => patchBoardInsightsWithItem(previous, updatedItem));
  };

  // Função combinada que atualiza dados e força re-render
  // Tarefa principal arrastada da lista e solta sobre um quadro da barra lateral
  const handleDropItemOnBoard = async (event, targetBoard) => {
    let payload = null;
    try { payload = JSON.parse(event.dataTransfer.getData('application/x-nexo-item')); } catch { /* payload inválido */ }
    if (!payload?.id) return;
    if (payload.boardId === targetBoard.id) { toast.info('A tarefa já está neste quadro'); return; }
    if (!window.confirm(`Mover "${payload.name}" (com as subtarefas) para o quadro "${targetBoard.name}"?`)) return;
    try {
      const { data } = await api.post(`/projects/items/${payload.id}/move-board`, { target_board_id: targetBoard.id });
      toast.success(`Tarefa movida para "${targetBoard.name}" (grupo ${data.group_title || ''})`);
      if (data.assignees_not_in_target?.length) {
        toast.warning(`Responsáveis que não são membros do quadro de destino: ${data.assignees_not_in_target.join(', ')}`);
      }
      await handleDataChange();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao mover a tarefa');
    }
  };

  const handleDataChange = async () => {
    setBoardDataRefreshing(true);
    try {
      await fetchBoardData();
      await fetchCustomColumns();
    } finally {
      setBoardDataRefreshing(false);
    }
  };

  const handleBoardCreated = (newBoard) => {
    setBoards([...boards, newBoard]);
    setSelectedBoard(newBoard);
    setShowCreateModal(false);
    toast.success('Quadro criado com sucesso!');
  };

  const handleTemplateSelected = (template) => {
    setShowTemplates(false);
    // Create board from template logic will be handled in BoardTemplates component
  };

  const handleFiltersApply = (filteredData) => {
    if (Array.isArray(filteredData)) {
      setActiveAdvancedFilter(null);
      return;
    }

    if (filteredData?.hasRules) {
      setActiveAdvancedFilter({
        ...filteredData,
        matchedParentIds: filteredData.matchedParentIds || [],
        matchedSubtaskIds: filteredData.matchedSubtaskIds || [],
        appliedRules: filteredData.appliedRules || [],
      });
      return;
    }

    setActiveAdvancedFilter(null);
  };

  const clearAdvancedFilters = () => {
    setActiveAdvancedFilter(null);
  };

  const handleRenameBoardSave = async () => {
    if (!boardNameInput.trim() || boardNameInput.trim() === selectedBoard?.name) {
      setEditingBoardName(false);
      return;
    }
    try {
      const res = await api.put(`/projects/boards/${selectedBoard.id}`, { name: boardNameInput.trim() });
      setSelectedBoard(res.data);
      setBoards(prev => prev.map(b => b.id === res.data.id ? res.data : b));
      setEditingBoardName(false);
      toast.success('Nome do quadro atualizado!');
    } catch {
      toast.error('Erro ao renomear quadro');
      setEditingBoardName(false);
    }
  };

  const handleRenameBoardById = async () => {
    if (!editingBoardId || !editingBoardNameInput.trim()) {
      setEditingBoardId(null);
      return;
    }
    const board = boards.find(b => b.id === editingBoardId);
    if (!board || editingBoardNameInput.trim() === board.name) {
      setEditingBoardId(null);
      return;
    }
    try {
      const res = await api.put(`/projects/boards/${editingBoardId}`, { name: editingBoardNameInput.trim() });
      setBoards(prev => prev.map(b => b.id === res.data.id ? res.data : b));
      if (selectedBoard?.id === res.data.id) setSelectedBoard(res.data);
      setEditingBoardId(null);
      toast.success('Nome do quadro atualizado!');
    } catch {
      toast.error('Erro ao renomear quadro');
      setEditingBoardId(null);
    }
  };

  const handleDropBoard = async (draggedId, targetId, targetGroupBoards, position = 'before') => {
    if (!draggedId || !targetId || draggedId === targetId) return;
    const draggedBoard = boards.find(b => b.id === draggedId);
    const targetBoard = boards.find(b => b.id === targetId);
    if (!draggedBoard || !targetBoard) return;

    const isCrossWorkspace = (draggedBoard.workspace_id || null) !== (targetBoard.workspace_id || null);

    if (isCrossWorkspace) {
      const newWorkspaceId = targetBoard.workspace_id || null;
      // Build new list for target workspace: insert dragged board before/after target
      const targetListWithout = targetGroupBoards.filter(b => b.id !== draggedId);
      const targetPosInList = targetListWithout.findIndex(b => b.id === targetId);
      const insertAt = position === 'after'
        ? (targetPosInList === -1 ? targetListWithout.length : targetPosInList + 1)
        : (targetPosInList === -1 ? targetListWithout.length : targetPosInList);
      const finalTargetList = [...targetListWithout];
      finalTargetList.splice(insertAt, 0, { ...draggedBoard, workspace_id: newWorkspaceId });

      // Optimistic state update: apply workspace_id + sort_order for all affected boards
      const finalOrderMap = new Map(finalTargetList.map((b, i) => [b.id, i]));
      setBoards(prev => prev.map(b => {
        if (b.id === draggedId) {
          const newSortOrder = finalOrderMap.get(draggedId) ?? b.sort_order;
          return { ...b, workspace_id: newWorkspaceId, sort_order: newSortOrder };
        }
        if (finalOrderMap.has(b.id)) return { ...b, sort_order: finalOrderMap.get(b.id) };
        return b;
      }));

      try {
        await api.put(`/projects/boards/${draggedId}`, { workspace_id: newWorkspaceId });
        const orderPayload = finalTargetList.map((b, i) => ({ board_id: b.id, sort_order: i }));
        await api.put('/projects/boards/reorder', orderPayload);
        const ws = workspaces.find(w => w.id === newWorkspaceId);
        toast.success(ws ? `Quadro movido para "${ws.name}"` : 'Quadro movido para Sem Workspace');
      } catch {
        toast.error('Erro ao mover quadro entre workspaces');
        fetchBoards();
      }
    } else {
      // Same workspace: reorder only
      const reordered = [...targetGroupBoards];
      const dragIdx = reordered.findIndex(b => b.id === draggedId);
      if (dragIdx === -1) return;
      const [moved] = reordered.splice(dragIdx, 1);
      // Find target position in the modified array (after removing dragged item)
      const newTargetIdx = reordered.findIndex(b => b.id === targetId);
      const insertIdx = position === 'after' ? newTargetIdx + 1 : newTargetIdx;
      reordered.splice(insertIdx < 0 ? 0 : insertIdx, 0, moved);
      const orderPayload = reordered.map((b, i) => ({ board_id: b.id, sort_order: i }));
      setBoards(prev => {
        const updated = [...prev];
        orderPayload.forEach(({ board_id, sort_order }) => {
          const idx = updated.findIndex(b => b.id === board_id);
          if (idx !== -1) updated[idx] = { ...updated[idx], sort_order };
        });
        return updated;
      });
      try {
        await api.put('/projects/boards/reorder', orderPayload);
      } catch {
        toast.error('Erro ao salvar ordem dos quadros');
      }
    }
  };

  const handleDropBoardOnWorkspace = async (draggedId, targetWorkspaceId) => {
    if (!draggedId) return;
    const draggedBoard = boards.find(b => b.id === draggedId);
    if (!draggedBoard) return;
    const newWorkspaceId = targetWorkspaceId || null;
    if ((draggedBoard.workspace_id || null) === newWorkspaceId) return;

    // Place at end of target workspace
    const targetList = boards
      .filter(b => (b.workspace_id || null) === newWorkspaceId && b.id !== draggedId)
      .slice()
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const finalList = [...targetList, { ...draggedBoard, workspace_id: newWorkspaceId }];

    const finalListOrderMap = new Map(finalList.map((b, i) => [b.id, i]));
    setBoards(prev => prev.map(b => {
      if (b.id === draggedId) {
        const newSortOrder = finalListOrderMap.get(draggedId) ?? b.sort_order;
        return { ...b, workspace_id: newWorkspaceId, sort_order: newSortOrder };
      }
      if (finalListOrderMap.has(b.id)) return { ...b, sort_order: finalListOrderMap.get(b.id) };
      return b;
    }));

    try {
      await api.put(`/projects/boards/${draggedId}`, { workspace_id: newWorkspaceId });
      const orderPayload = finalList.map((b, i) => ({ board_id: b.id, sort_order: i }));
      await api.put('/projects/boards/reorder', orderPayload);
      const ws = workspaces.find(w => w.id === newWorkspaceId);
      toast.success(ws ? `Quadro movido para "${ws.name}"` : 'Quadro movido para Sem Workspace');
    } catch {
      toast.error('Erro ao mover quadro entre workspaces');
      fetchBoards();
    }
  };

  const handleDeleteBoard = async () => {
    if (!deletingBoardId) return;
    try {
      await api.delete(`/projects/boards/${deletingBoardId}`);
      setBoards(prev => prev.filter(b => b.id !== deletingBoardId));
      if (selectedBoard?.id === deletingBoardId) {
        setSelectedBoard(null);
        setBoardGroups([]);
        setBoardItems([]);
        setFilteredItems([]);
        setBoardInsights(null);
        setFilteredInsights(null);
        setActiveAdvancedFilter(null);
      }
      setDeletingBoardId(null);
      setDeletingBoardName('');
      toast.success('Quadro excluído com sucesso!');
    } catch {
      toast.error('Erro ao excluir quadro');
      setDeletingBoardId(null);
      setDeletingBoardName('');
    }
  };

  const handleMoveToWorkspace = async () => {
    if (!selectedBoard) return;
    try {
      const payload = { workspace_id: moveToWorkspaceId || null };
      const res = await api.put(`/projects/boards/${selectedBoard.id}`, payload);
      setBoards(prev => prev.map(b => b.id === selectedBoard.id ? { ...b, workspace_id: moveToWorkspaceId || null } : b));
      setSelectedBoard(prev => ({ ...prev, workspace_id: moveToWorkspaceId || null }));
      const ws = workspaces.find(w => w.id === moveToWorkspaceId);
      toast.success(ws ? `Quadro movido para "${ws.name}"` : 'Quadro removido do workspace');
      setShowMoveWorkspace(false);
      setMoveToWorkspaceId('');
    } catch {
      toast.error('Erro ao mover quadro');
    }
  };

  // ── Phase 2: Search ──────────────────────────────────────────────────────────
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.trim().length < 2) {
      setSearchResults(null);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await api.get(`/projects/search?q=${encodeURIComponent(query)}`);
      setSearchResults(res.data);
    } catch (e) {
      setSearchResults(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchItemClick = (item) => {
    const board = boards.find(b => b.id === item.board_id);
    if (board) { setSelectedBoard(board); setCurrentView('table'); }
    setSearchQuery('');
    setSearchResults(null);
    setShowSearch(false);
  };

  // ── Phase 2: Favorites ───────────────────────────────────────────────────────
  const toggleFavorite = async (board) => {
    const isFav = board.is_favorited;
    try {
      if (isFav) {
        await api.delete(`/projects/boards/${board.id}/favorite`);
      } else {
        await api.post(`/projects/boards/${board.id}/favorite`);
      }
      setBoards(prev => prev.map(b =>
        b.id === board.id ? { ...b, is_favorited: !isFav } : b
      ).sort((a, b) => {
        const fa = b.id === board.id ? !isFav : b.is_favorited;
        const fb = a.id === board.id ? !isFav : a.is_favorited;
        if (fa !== fb) return fa ? 1 : -1;
        return a.name.localeCompare(b.name);
      }));
      if (selectedBoard?.id === board.id) {
        setSelectedBoard(prev => ({ ...prev, is_favorited: !isFav }));
      }
      toast.success(isFav ? 'Removido dos favoritos' : 'Adicionado aos favoritos');
    } catch (e) {
      toast.error('Erro ao atualizar favorito');
    }
  };

  // ── Phase 2: Share (Public Board) ────────────────────────────────────────────
  const togglePublic = async () => {
    if (!selectedBoard) return;
    const newPublic = !selectedBoard.is_public;
    try {
      await api.put(`/projects/boards/${selectedBoard.id}/visibility?is_public=${newPublic}`);
      setSelectedBoard(prev => ({ ...prev, is_public: newPublic }));
      setBoards(prev => prev.map(b => b.id === selectedBoard.id ? { ...b, is_public: newPublic } : b));
      if (newPublic) {
        const url = `${window.location.origin}/public/board/${selectedBoard.id}`;
        navigator.clipboard.writeText(url).catch(() => {});
        toast.success('Quadro público! Link copiado para a área de transferência.');
      } else {
        toast.success('Quadro agora é privado.');
      }
    } catch (e) {
      toast.error('Erro ao alterar visibilidade');
    }
  };

  const copyShareLink = () => {
    if (!selectedBoard) return;
    const url = `${window.location.origin}/public/board/${selectedBoard.id}`;
    navigator.clipboard.writeText(url).catch(() => {});
    toast.success('Link copiado!');
  };

  const getViewIcon = (view) => {
    switch (view) {
      case 'dashboard':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      case 'table':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M3 6h18m-9 8h9m-9 4h9m-9-8h9" />
          </svg>
        );
      case 'kanban':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10z" />
          </svg>
        );
      case 'timeline':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      case 'calendar':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando projetos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`nexo-project-shell min-h-screen bg-gray-50 ${projectDarkMode ? 'nexo-project-dark' : ''}`}>
      {/* Modern Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
          {/* Board Selector & Navigation */}
          <div className="flex flex-wrap items-center justify-between gap-y-3">
            <div className="flex flex-wrap items-center gap-3">
              {/* Título da página + Board Selector */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center shadow-sm">
                    <LayoutDashboard className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-slate-900 leading-tight">Projetos</h1>
                    <p className="text-xs text-slate-500">Quadros, tarefas e colaboração da equipe</p>
                  </div>
                </div>
              </div>
              
              {/* Board Selector with Workspace Hierarchy */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <div className="flex items-center space-x-1">
                  {editingBoardName ? (
                    <input
                      autoFocus
                      value={boardNameInput}
                      onChange={e => setBoardNameInput(e.target.value)}
                      onBlur={handleRenameBoardSave}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleRenameBoardSave();
                        if (e.key === 'Escape') setEditingBoardName(false);
                      }}
                      className="w-40 sm:w-56 md:w-64 text-lg font-semibold border border-blue-400 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-300"
                    />
                  ) : (
                    <div className="relative" ref={boardDropdownRef}>
                      {/* Custom dropdown trigger */}
                      <button
                        ref={boardTriggerRef}
                        onClick={() => { setBoardDropdownOpen(o => !o); setEditingBoardId(null); setShowWorkspaceForm(false); setShowMoveWorkspace(false); }}
                        className="flex items-center space-x-2 w-40 sm:w-56 md:w-72 px-3 py-1.5 rounded-lg hover:bg-gray-100 text-lg font-semibold text-gray-800 transition-colors"
                      >
                        <span className="flex-1 text-left truncate">{selectedBoard ? selectedBoard.name : 'Selecionar Quadro'}</span>
                        <svg className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${boardDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {/* Custom dropdown panel */}
                      {boardDropdownOpen && (
                        <div className="absolute top-full left-0 mt-1 z-[200] bg-white border border-gray-200 rounded-xl shadow-xl w-80 max-h-[400px] overflow-y-auto py-1">
                          {(() => {
                            // Compute flat board list for keyboard navigation
                            let flatBoards = [];
                            if (adminViewActive && isAdmin) {
                              const ownerGroups = {};
                              boards.forEach(b => { const k = b.owner_name || '—'; if (!ownerGroups[k]) ownerGroups[k] = []; ownerGroups[k].push(b); });
                              Object.entries(ownerGroups).sort(([a], [b]) => a.localeCompare(b)).forEach(([, bs]) => flatBoards.push(...bs));
                            } else if (workspaces.length > 0) {
                              workspaces.forEach(ws => {
                                const wsBoards = boards.filter(b => b.workspace_id === ws.id).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
                                flatBoards.push(...wsBoards);
                              });
                              const noWsBoards = boards.filter(b => !b.workspace_id || !workspaces.find(w => w.id === b.workspace_id)).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
                              flatBoards.push(...noWsBoards);
                            } else {
                              flatBoards = boards.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
                            }
                            flatBoardsRef.current = flatBoards;

                            const clearDragOver = () => {
                              const prevBoardId = dragOverBoardIdRef.current;
                              const prevPosition = dragOverPositionRef.current;
                              const prevDragging = dragBoardRef.current;
                              if (prevBoardId) {
                                setExitingDrop({ boardId: prevBoardId, position: prevPosition, dragging: prevDragging });
                                clearTimeout(exitingDropTimerRef.current);
                                exitingDropTimerRef.current = setTimeout(() => setExitingDrop(null), 180);
                              }
                              dragOverBoardIdRef.current = null;
                              dragOverPositionRef.current = null;
                              setDragOverBoardId(null);
                              setDragOverPosition(null);
                            };

                            const DropLine = ({ height = dragBoardHeightRef.current, exiting = false }) => {
                              const [expanded, setExpanded] = React.useState(exiting);
                              React.useEffect(() => {
                                const raf = requestAnimationFrame(() => setExpanded(!exiting));
                                return () => cancelAnimationFrame(raf);
                              }, [exiting]);
                              return (
                                <div
                                  className="mx-1 my-0.5 pointer-events-none overflow-hidden"
                                  style={{
                                    height: expanded ? `${height}px` : '0px',
                                    transition: 'height 150ms ease',
                                  }}
                                >
                                  <div
                                    className="h-full rounded-lg border-2 border-dashed border-blue-400 flex items-center justify-center"
                                    style={{
                                      background: 'rgba(139,92,246,0.06)',
                                      opacity: expanded ? 1 : 0,
                                      transition: 'opacity 150ms ease',
                                    }}
                                  >
                                    {!exiting && (
                                      <span className="text-[11px] text-blue-400 select-none font-medium tracking-wide">
                                        ↕ soltar aqui
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            };

                            const BoardItem = ({ board, indent, groupBoards, itemRef }) => {
                              const idx = flatBoards.findIndex(b => b.id === board.id);
                              const isFocused = idx !== -1 && focusedBoardIdx === idx;
                              return (
                              <div
                                key={board.id}
                                ref={itemRef}
                                draggable={editingBoardId !== board.id}
                                onDragStart={e => { dragBoardRef.current = board.id; dragBoardWorkspaceRef.current = board.workspace_id || null; dragBoardHeightRef.current = e.currentTarget.getBoundingClientRect().height; e.dataTransfer.effectAllowed = 'move'; }}
                                onDragEnd={() => { clearDragOver(); dragBoardRef.current = null; dragBoardWorkspaceRef.current = null; setDragOverWorkspaceId(null); }}
                                onDragOver={e => { if (!dragBoardRef.current && e.dataTransfer.types.includes('application/x-nexo-item')) { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; if (itemDropBoardId !== board.id) setItemDropBoardId(board.id); return; } e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; const rect = e.currentTarget.getBoundingClientRect(); const pos = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after'; dragOverPositionRef.current = pos; dragOverBoardIdRef.current = board.id; setDragOverPosition(pos); setDragOverBoardId(board.id); setDragOverWorkspaceId(null); }}
                                onDragLeave={() => { setItemDropBoardId(null); dragOverBoardIdRef.current = null; dragOverPositionRef.current = null; setDragOverBoardId(null); setDragOverPosition(null); }}
                                onDrop={e => { if (!dragBoardRef.current && e.dataTransfer.types.includes('application/x-nexo-item')) { e.preventDefault(); e.stopPropagation(); setItemDropBoardId(null); handleDropItemOnBoard(e, board); return; } e.preventDefault(); e.stopPropagation(); const pos = dragOverPositionRef.current || 'before'; clearDragOver(); handleDropBoard(dragBoardRef.current, board.id, groupBoards || boards, pos); }}
                                className={`flex items-center group px-3 py-1.5 rounded-lg mx-1 cursor-pointer transition-colors ${itemDropBoardId === board.id ? 'bg-green-100 ring-2 ring-inset ring-green-400' : ''} ${isFocused ? 'bg-blue-100 ring-1 ring-inset ring-blue-300' : selectedBoard?.id === board.id ? 'bg-blue-50' : 'hover:bg-gray-50'} ${indent ? 'pl-5' : ''}`}
                              >
                                {editingBoardId === board.id ? (
                                  <>
                                    <input
                                      autoFocus
                                      value={editingBoardNameInput}
                                      onChange={e => setEditingBoardNameInput(e.target.value)}
                                      onBlur={handleRenameBoardById}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter') handleRenameBoardById();
                                        if (e.key === 'Escape') setEditingBoardId(null);
                                      }}
                                      onClick={e => e.stopPropagation()}
                                      className="flex-1 text-sm border border-blue-400 rounded px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-300 min-w-0"
                                    />
                                    <button
                                      onClick={e => { e.stopPropagation(); setEditingBoardId(null); }}
                                      className="ml-1 p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 flex-shrink-0"
                                      title="Cancelar"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <span
                                      className="text-gray-300 hover:text-gray-500 mr-1 cursor-grab active:cursor-grabbing flex-shrink-0 select-none opacity-0 group-hover:opacity-100 transition-opacity"
                                      title="Arrastar para reordenar"
                                    >⠿</span>
                                    <div
                                      className="flex items-center space-x-2 flex-1 min-w-0"
                                      onClick={() => { setSelectedBoard(board); setBoardDropdownOpen(false); setEditingBoardId(null); }}
                                    >
                                      {board.is_favorited ? <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400 flex-shrink-0" /> : <div className="w-2.5 h-2.5 bg-blue-500 rounded-full flex-shrink-0"></div>}
                                      <span className="truncate text-sm text-gray-800">{board.name}</span>
                                      {board.is_public && <Share2 className="w-3 h-3 text-green-500 flex-shrink-0" />}
                                      {board.owner_name && adminViewActive && isAdmin && (
                                        <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded-full leading-none flex-shrink-0">
                                          {board.owner_name.split(' ')[0]}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center space-x-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <button
                                        onClick={e => { e.stopPropagation(); setEditingBoardId(board.id); setEditingBoardNameInput(board.name); }}
                                        className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                                        title="Renomear quadro"
                                      >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                        </svg>
                                      </button>
                                      <button
                                        onClick={e => { e.stopPropagation(); setDeletingBoardId(board.id); setDeletingBoardName(board.name); setBoardDropdownOpen(false); }}
                                        className="p-0.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-500"
                                        title="Excluir quadro"
                                      >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                            };

                            if (adminViewActive && isAdmin) {
                              const ownerGroups = {};
                              boards.forEach(b => {
                                const key = b.owner_name || '—';
                                if (!ownerGroups[key]) ownerGroups[key] = [];
                                ownerGroups[key].push(b);
                              });
                              return Object.entries(ownerGroups).sort(([a], [b]) => a.localeCompare(b)).map(([ownerName, ownerBoards]) => (
                                <div key={ownerName}>
                                  <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-blue-400 flex items-center space-x-1">
                                    <UsersIcon className="w-3 h-3" /><span>{ownerName}</span>
                                  </div>
                                  {ownerBoards.map((board, idx) => (
                                    <React.Fragment key={board.id}>
                                      {dragOverBoardId === board.id && dragOverPosition === 'before' && dragBoardRef.current !== board.id && <DropLine height={dragBoardHeightRef.current} />}
                                      {exitingDrop?.boardId === board.id && exitingDrop?.position === 'before' && exitingDrop?.dragging !== board.id && <DropLine height={dragBoardHeightRef.current} exiting />}
                                      <BoardItem board={board} indent groupBoards={ownerBoards} itemRef={el => { if (el) boardItemRefsMap.current[board.id] = el; else delete boardItemRefsMap.current[board.id]; }} />
                                      {dragOverBoardId === board.id && dragOverPosition === 'after' && dragBoardRef.current !== board.id && <DropLine height={dragBoardHeightRef.current} />}
                                      {exitingDrop?.boardId === board.id && exitingDrop?.position === 'after' && exitingDrop?.dragging !== board.id && <DropLine height={dragBoardHeightRef.current} exiting />}
                                    </React.Fragment>
                                  ))}
                                </div>
                              ));
                            } else if (workspaces.length > 0) {
                              return (
                                <>
                                  {workspaces.map(ws => {
                                    const wsBoards = boards.filter(b => b.workspace_id === ws.id).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
                                    const isDropTarget = dragOverWorkspaceId === ws.id && dragBoardRef.current && dragBoardWorkspaceRef.current !== ws.id;
                                    return (
                                      <div key={ws.id}
                                        onDragOver={e => { if (dragBoardRef.current) { e.preventDefault(); setDragOverWorkspaceId(ws.id); setDragOverBoardId(null); } }}
                                        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverWorkspaceId(null); }}
                                        onDrop={e => { e.preventDefault(); setDragOverWorkspaceId(null); clearDragOver(); if (dragBoardRef.current) handleDropBoardOnWorkspace(dragBoardRef.current, ws.id); }}
                                        className={`rounded-lg transition-colors ${isDropTarget ? 'bg-blue-50 ring-1 ring-blue-300' : ''}`}
                                      >
                                        <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center space-x-1">
                                          <Building2 className="w-3 h-3" /><span>{ws.name}</span>
                                          {isDropTarget && <span className="text-blue-400 ml-1">↓ soltar aqui</span>}
                                        </div>
                                        {wsBoards.map((board) => (
                                          <React.Fragment key={board.id}>
                                            {dragOverBoardId === board.id && dragOverPosition === 'before' && dragBoardRef.current !== board.id && <DropLine height={dragBoardHeightRef.current} />}
                                            {exitingDrop?.boardId === board.id && exitingDrop?.position === 'before' && exitingDrop?.dragging !== board.id && <DropLine height={dragBoardHeightRef.current} exiting />}
                                            <BoardItem board={board} indent groupBoards={wsBoards} itemRef={el => { if (el) boardItemRefsMap.current[board.id] = el; else delete boardItemRefsMap.current[board.id]; }} />
                                            {dragOverBoardId === board.id && dragOverPosition === 'after' && dragBoardRef.current !== board.id && <DropLine height={dragBoardHeightRef.current} />}
                                            {exitingDrop?.boardId === board.id && exitingDrop?.position === 'after' && exitingDrop?.dragging !== board.id && <DropLine height={dragBoardHeightRef.current} exiting />}
                                          </React.Fragment>
                                        ))}
                                        {wsBoards.length === 0 && isDropTarget && (
                                          <div className="mx-2 mb-1 px-3 py-2 border-2 border-dashed border-blue-300 rounded-lg text-xs text-blue-400 text-center">
                                            Soltar aqui
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                  {(() => {
                                    const noWsBoards = boards.filter(b => !b.workspace_id || !workspaces.find(w => w.id === b.workspace_id)).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
                                    const isNoWsDropTarget = dragOverWorkspaceId === '__none__' && dragBoardRef.current && dragBoardWorkspaceRef.current !== null;
                                    return (noWsBoards.length > 0 || isNoWsDropTarget) ? (
                                      <div
                                        onDragOver={e => { if (dragBoardRef.current) { e.preventDefault(); setDragOverWorkspaceId('__none__'); setDragOverBoardId(null); } }}
                                        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverWorkspaceId(null); }}
                                        onDrop={e => { e.preventDefault(); setDragOverWorkspaceId(null); clearDragOver(); if (dragBoardRef.current) handleDropBoardOnWorkspace(dragBoardRef.current, null); }}
                                        className={`rounded-lg transition-colors ${isNoWsDropTarget ? 'bg-blue-50 ring-1 ring-blue-300' : ''}`}
                                      >
                                        <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center space-x-1">
                                          <span>Sem Workspace</span>
                                          {isNoWsDropTarget && <span className="text-blue-400 ml-1">↓ soltar aqui</span>}
                                        </div>
                                        {noWsBoards.map((board) => (
                                          <React.Fragment key={board.id}>
                                            {dragOverBoardId === board.id && dragOverPosition === 'before' && dragBoardRef.current !== board.id && <DropLine height={dragBoardHeightRef.current} />}
                                            {exitingDrop?.boardId === board.id && exitingDrop?.position === 'before' && exitingDrop?.dragging !== board.id && <DropLine height={dragBoardHeightRef.current} exiting />}
                                            <BoardItem board={board} groupBoards={noWsBoards} itemRef={el => { if (el) boardItemRefsMap.current[board.id] = el; else delete boardItemRefsMap.current[board.id]; }} />
                                            {dragOverBoardId === board.id && dragOverPosition === 'after' && dragBoardRef.current !== board.id && <DropLine height={dragBoardHeightRef.current} />}
                                            {exitingDrop?.boardId === board.id && exitingDrop?.position === 'after' && exitingDrop?.dragging !== board.id && <DropLine height={dragBoardHeightRef.current} exiting />}
                                          </React.Fragment>
                                        ))}
                                        {noWsBoards.length === 0 && isNoWsDropTarget && (
                                          <div className="mx-2 mb-1 px-3 py-2 border-2 border-dashed border-blue-300 rounded-lg text-xs text-blue-400 text-center">
                                            Soltar aqui
                                          </div>
                                        )}
                                      </div>
                                    ) : null;
                                  })()}
                                </>
                              );
                            } else {
                              const sortedBoards = boards.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
                              return sortedBoards.map((board) => (
                                <React.Fragment key={board.id}>
                                  {dragOverBoardId === board.id && dragOverPosition === 'before' && dragBoardRef.current !== board.id && <DropLine height={dragBoardHeightRef.current} />}
                                  {exitingDrop?.boardId === board.id && exitingDrop?.position === 'before' && exitingDrop?.dragging !== board.id && <DropLine height={dragBoardHeightRef.current} exiting />}
                                  <BoardItem board={board} groupBoards={sortedBoards} itemRef={el => { if (el) boardItemRefsMap.current[board.id] = el; else delete boardItemRefsMap.current[board.id]; }} />
                                  {dragOverBoardId === board.id && dragOverPosition === 'after' && dragBoardRef.current !== board.id && <DropLine height={dragBoardHeightRef.current} />}
                                  {exitingDrop?.boardId === board.id && exitingDrop?.position === 'after' && exitingDrop?.dragging !== board.id && <DropLine height={dragBoardHeightRef.current} exiting />}
                                </React.Fragment>
                              ));
                            }
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                  {selectedBoard && !editingBoardName && (
                    <button
                      onClick={() => { setBoardNameInput(selectedBoard.name); setEditingBoardName(true); }}
                      className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                      title="Renomear quadro"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Workspace Manager Pill */}
                <div className="relative">
                  <button
                    ref={workspaceFormTriggerRef}
                    onClick={() => { setShowWorkspaceForm(f => !f); setBoardDropdownOpen(false); setShowMoveWorkspace(false); }}
                    className="flex items-center space-x-1 text-xs text-gray-500 hover:text-blue-600 px-2 py-1 rounded-lg hover:bg-blue-50 border border-gray-200 transition-colors"
                    title="Gerenciar Workspaces"
                  >
                    <Building2 className="w-3.5 h-3.5" />
                    <span className="font-medium">Workspaces ({workspaces.length})</span>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </button>
                  {showWorkspaceForm && (
                    <div ref={workspaceFormRef} className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-3 min-w-[240px]">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Workspaces</p>
                      <div className="space-y-1 mb-2 max-h-40 overflow-y-auto">
                        {workspaces.map((ws, idx) => {
                          const isFocused = focusedWsIdx === idx;
                          return (
                          <div key={ws.id}
                            className={`flex items-center justify-between px-2 py-1.5 rounded-lg group cursor-pointer transition-colors ${isFocused ? 'bg-blue-100 ring-1 ring-inset ring-blue-300' : selectedWorkspace?.id === ws.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-blue-50'}`}
                            onClick={() => { setSelectedWorkspace(ws); setShowWorkspaceForm(false); }}>
                            <span className="text-sm text-gray-700 flex-1 flex items-center gap-1">
                              {selectedWorkspace?.id === ws.id ? <Check className="w-3.5 h-3.5 text-blue-600" /> : <Building2 className="w-3.5 h-3.5 text-slate-400" />} {ws.name}
                            </span>
                            <button
                              className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 text-xs ml-2"
                              onClick={e => { e.stopPropagation(); handleDeleteWorkspace(ws); }}
                              title="Excluir workspace"
                            >✕</button>
                          </div>
                          );
                        })}
                        {workspaces.length === 0 && <p className="text-xs text-gray-400 text-center py-2">Nenhum workspace ainda</p>}
                      </div>
                      <div className="border-t border-gray-100 pt-2 flex space-x-1">
                        <input
                          className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                          placeholder="Nome do workspace..."
                          value={newWorkspaceName}
                          onChange={e => setNewWorkspaceName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleCreateWorkspace(); if (e.key === 'Escape') setShowWorkspaceForm(false); }}
                        />
                        <button
                          onClick={handleCreateWorkspace}
                          className="text-xs bg-blue-600 text-white px-2 py-1 rounded-lg hover:bg-blue-700"
                        >+ Criar</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Board Stats */}
              {selectedBoard && (
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span>{boardMembers.length} membros</span>
                  <span>•</span>
                  <span>{boardItems.length} itens</span>
                  {boardGroups.length > 0 && (
                    <>
                      <span>•</span>
                      <span>{boardGroups.length} grupos</span>
                    </>
                  )}
                  {adminViewActive && selectedBoard.owner_name && (
                    <>
                      <span>•</span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-full text-xs text-blue-700 font-medium">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                        {selectedBoard.owner_name}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto min-w-0">
              {/* Phase 2 — Global Search */}
              <div className="relative">
                {showSearch ? (
                  <div className="flex items-center space-x-1">
                    <Input
                      autoFocus
                      className="w-36 sm:w-64 h-8 text-sm"
                      placeholder="Buscar quadros e itens..."
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Escape') { setShowSearch(false); setSearchQuery(''); setSearchResults(null); } }}
                    />
                    <button onClick={() => { setShowSearch(false); setSearchQuery(''); setSearchResults(null); }} className="text-gray-400 hover:text-gray-600 text-lg px-1">×</button>
                  </div>
                ) : (
                  <button onClick={() => setShowSearch(true)} className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700" title="Pesquisa global">
                    <Search className="w-5 h-5" />
                  </button>
                )}

                {/* Search Results Overlay */}
                {searchResults && (searchResults.boards.length > 0 || searchResults.items.length > 0) && (
                  <div className="absolute top-full right-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden">
                    {searchLoading && <p className="text-xs text-gray-400 px-3 py-2">Buscando...</p>}
                    {searchResults.boards.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 px-3 pt-2 pb-1 uppercase tracking-wide">Quadros</p>
                        {searchResults.boards.map(b => (
                          <button key={b.id} className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center space-x-2"
                            onClick={() => { const board = boards.find(bd => bd.id === b.id); if (board) { setSelectedBoard(board); setCurrentView('table'); } setShowSearch(false); setSearchQuery(''); setSearchResults(null); }}>
                            <Table2 className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-gray-800">{b.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {searchResults.items.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 px-3 pt-2 pb-1 uppercase tracking-wide">Itens</p>
                        {searchResults.items.map(item => (
                          <button key={item.id} className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center space-x-2"
                            onClick={() => handleSearchItemClick(item)}>
                            <KanbanSquare className="w-4 h-4 text-slate-400" />
                            <div>
                              <p className="text-sm text-gray-800">{item.name}</p>
                              {item.status && <p className="text-xs text-gray-400">{item.status}</p>}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {searchResults && searchResults.boards.length === 0 && searchResults.items.length === 0 && searchQuery.trim().length >= 2 && (
                  <div className="absolute top-full right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-50 px-3 py-3 text-sm text-gray-500">
                    Nenhum resultado para "{searchQuery}"
                  </div>
                )}
              </div>

              {/* Phase 2 — Favorite current board */}
              {selectedBoard && (
                <button
                  onClick={() => toggleFavorite(selectedBoard)}
                  className={`p-1.5 rounded-md transition-colors ${selectedBoard.is_favorited ? 'text-yellow-400 hover:text-yellow-500' : 'text-gray-400 hover:text-yellow-400'}`}
                  title={selectedBoard.is_favorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                >
                  <Star className={`w-5 h-5 ${selectedBoard.is_favorited ? 'fill-current' : ''}`} />
                </button>
              )}

              {/* Phase 2 — Share / Public board */}
              {selectedBoard && (
                <div className="relative group">
                  <button
                    onClick={togglePublic}
                    className={`p-1.5 rounded-md transition-colors ${selectedBoard.is_public ? 'text-green-500 hover:text-green-600' : 'text-gray-400 hover:text-gray-600'}`}
                    title={selectedBoard.is_public ? 'Quadro público — clique para tornar privado' : 'Tornar quadro público e compartilhar'}
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                  {selectedBoard.is_public && (
                    <button
                      onClick={copyShareLink}
                      className="absolute top-full right-0 mt-1 hidden group-hover:flex items-center space-x-1 whitespace-nowrap bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg z-50"
                    >
                      Copiar link público
                    </button>
                  )}
                </div>
              )}

              {/* Delete current board */}
              {selectedBoard && (
                <button
                  onClick={() => { setDeletingBoardId(selectedBoard.id); setDeletingBoardName(selectedBoard.name); }}
                  className="p-1.5 rounded-md text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                  title="Excluir quadro"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}

              {/* View Selector + Admin + Novo Quadro — stack as their own row on mobile */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto min-w-0">
                {selectedBoard && (
                  <div className="flex items-center bg-gray-100 rounded-lg p-1 gap-1 overflow-x-auto max-w-full min-w-0 w-full sm:w-auto nx-scrollbar">
                    {[
                      { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
                      { key: 'table', label: 'Main Table', icon: Table2 },
                      { key: 'kanban', label: 'Kanban', icon: KanbanSquare },
                      { key: 'workload', label: 'Workload', icon: Zap },
                      { key: 'members', label: 'Membros', icon: UsersIcon },
                      { key: 'portfolio', label: 'Portfolio', icon: FolderKanban },
                      { key: 'goals', label: 'Metas', icon: Target },
                    ].map((view) => {
                      const ViewIcon = view.icon;
                      return (
                        <button
                          key={view.key}
                          onClick={() => setCurrentView(view.key)}
                          title={view.label}
                          className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap shrink-0 ${
                            currentView === view.key
                              ? 'bg-white text-blue-600 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <ViewIcon className="w-4 h-4" />
                          <span className={currentView === view.key ? 'inline' : 'hidden sm:inline'}>{view.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
                  {/* Admin Toggle (only for admins) */}
                  {isAdmin && (
                    <button
                      onClick={() => {
                        const next = !adminViewActive;
                        setAdminViewActive(next);
                        fetchBoards(next);
                      }}
                      title={adminViewActive ? 'Sair da visão admin' : 'Ativar visão admin (ver todos os quadros)'}
                      className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all shrink-0 whitespace-nowrap ${
                        adminViewActive
                          ? 'bg-blue-100 border-blue-400 text-blue-700'
                          : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-blue-300 hover:text-blue-600'
                      }`}
                    >
                      <Eye className="w-3.5 h-3.5" />
                      <span>{adminViewActive ? 'Admin ON' : 'Admin'}</span>
                    </button>
                  )}

                  {/* Action Buttons */}
                  <Button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 shrink-0 whitespace-nowrap flex-1 sm:flex-none justify-center"
                  >
                    <span className="mr-2">+</span>
                    <span>Novo Quadro</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Admin mode banner */}
      {adminViewActive && isAdmin && (
        <div className="bg-blue-50 border-b border-blue-200 px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span className="font-semibold">Modo Admin ativo</span>
            <span className="text-blue-500">— você está visualizando todos os quadros do sistema ({boards.length} quadros)</span>
          </div>
          <button
            onClick={() => { setAdminViewActive(false); fetchBoards(false); setSelectedBoard(null); }}
            className="text-xs text-blue-500 hover:text-blue-700 underline"
          >
            Sair do modo admin
          </button>
        </div>
      )}

      {/* Main Content - Compact Layout */}
      <div className="px-6 py-6">{/* Reduced from px-8 py-8 */}
        {!selectedBoard ? (
          /* Welcome State */
          <div className="text-center py-16 max-w-2xl mx-auto">
            <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <FolderKanban className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Sistema de Projetos Moderno
            </h2>
            <p className="text-gray-600 mb-8">
              Gerencie seus projetos com eficiência. Layout compacto inspirado no Monday.com com cores, status visuais e colaboração em equipe.
            </p>
            <div className="flex justify-center space-x-4">
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 hover:bg-blue-700 px-6 py-3 text-lg"
              >
                Criar Primeiro Quadro
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowTemplates(true)}
                className="px-6 py-3 text-lg"
              >
                Templates
              </Button>
            </div>
          </div>
        ) : (
          /* Board Content */
          <div className="space-y-6">{/* Reduced spacing */}
            {/* Secondary Toolbar - Compact Actions */}
            {selectedBoard && (
              <div className="flex flex-wrap items-center bg-white rounded-lg border border-gray-200 px-4 py-3 shadow-sm gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowFilters(true)}
                    className="group text-gray-600 hover:text-gray-900"
                  >
                    <Filter className="w-4 h-4 mr-1.5 transition-colors group-hover:text-blue-500" />
                    Filtros
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowGroupCustomization(true)}
                    className="group text-gray-600 hover:text-gray-900"
                  >
                    <Palette className="w-4 h-4 mr-1.5 transition-colors group-hover:text-pink-500" />
                    Personalizar
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setCurrentView('columns')}
                    className={`group text-gray-600 hover:text-gray-900 ${currentView === 'columns' ? 'bg-blue-50 text-blue-600' : ''}`}
                  >
                    <Columns3 className="w-4 h-4 mr-1.5 transition-colors group-hover:text-teal-500" />
                    Colunas
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setCurrentView('tags')}
                    className={`group text-gray-600 hover:text-gray-900 ${currentView === 'tags' ? 'bg-blue-50 text-blue-600' : ''}`}
                  >
                    <Tags className="w-4 h-4 mr-1.5 transition-colors group-hover:text-amber-500" />
                    Tags
                  </Button>
                </div>

                <div className="hidden sm:block h-5 w-px bg-gray-200" />

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentView('automations')}
                    className={`group text-gray-600 hover:text-gray-900 ${currentView === 'automations' ? 'bg-blue-50 text-blue-600' : ''}`}
                  >
                    <Zap className="w-4 h-4 mr-1.5 transition-colors group-hover:text-orange-500" />
                    Automações
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setCurrentView('webhooks')}
                    className={`group text-gray-600 hover:text-gray-900 ${currentView === 'webhooks' ? 'bg-blue-50 text-blue-600' : ''}`}
                  >
                    <Webhook className="w-4 h-4 mr-1.5 transition-colors group-hover:text-purple-500" />
                    Webhooks
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowAnalytics(true)}
                    className="group text-gray-600 hover:text-gray-900"
                  >
                    <BarChart3 className="w-4 h-4 mr-1.5 transition-colors group-hover:text-green-500" />
                    Relatórios
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowTemplates(true)}
                    className="group text-gray-600 hover:text-gray-900"
                  >
                    <LayoutTemplate className="w-4 h-4 mr-1.5 transition-colors group-hover:text-red-500" />
                    Templates
                  </Button>
                  {workspaces.length > 0 && (
                    <div className="relative">
                      <Button
                        ref={moveWorkspaceTriggerRef}
                        variant="ghost"
                        size="sm"
                        onClick={() => { setShowMoveWorkspace(f => !f); setMoveToWorkspaceId(selectedBoard?.workspace_id || ''); setBoardDropdownOpen(false); setShowWorkspaceForm(false); }}
                        className="group text-gray-600 hover:text-gray-900"
                        title="Mover para outro workspace"
                      >
                        <Building2 className="w-4 h-4 mr-1.5" />
                        Mover
                      </Button>
                      {showMoveWorkspace && (
                        <div ref={moveWorkspaceRef} className="absolute top-full right-0 mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-3 min-w-[220px]">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Mover para Workspace</p>
                          <select
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-400"
                            value={moveToWorkspaceId}
                            onChange={e => setMoveToWorkspaceId(e.target.value)}
                          >
                            <option value="">— Sem workspace —</option>
                            {workspaces.map(ws => (
                              <option key={ws.id} value={ws.id}>{ws.name}</option>
                            ))}
                          </select>
                          <div className="flex justify-end gap-2">
                            <button onClick={() => setShowMoveWorkspace(false)} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1">Cancelar</button>
                            <button onClick={handleMoveToWorkspace} className="text-xs bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700">Mover</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <NotificationBell api={api} />
                </div>
              </div>
            )}

            {currentView === 'dashboard' && (
              <ProjectDashboard 
                board={selectedBoard}
                insights={boardInsights}
                onRefresh={handleDashboardRefresh}
              />
            )}
            
            {currentView === 'table' && (
              <ModernProjectTable 
                board={selectedBoard}
                groups={boardGroups}
                items={filteredItems}
                insights={filteredInsights || boardInsights}
                advancedFilter={activeAdvancedFilter}
                members={boardMembers}
                customColumns={customColumns}
                boardListSummary={boardListSummary}
                boardRefreshing={boardDataRefreshing}
                api={api}
                currentUser={user}
                darkMode={projectDarkMode}
                onlyMyTasks={onlyMyTasks}
                onToggleOnlyMyTasks={() => setOnlyMyTasks(value => !value)}
                onClearAdvancedFilters={clearAdvancedFilters}
                onItemPatched={handleItemPatched}
                onDataChange={handleDataChange}
                onManageFields={() => setCurrentView('columns')}
              />
            )}
            
            {currentView === 'kanban' && (
              <ProjectKanban 
                board={selectedBoard}
                groups={boardGroups}
                items={filteredItems}
                members={boardMembers}
                insights={filteredInsights || boardInsights}
                customColumns={customColumns}
                api={api}
                darkMode={projectDarkMode}
                onDataChange={fetchBoardData}
              />
            )}
            
            {currentView === 'timeline' && (
              <ProjectTimeline 
                board={selectedBoard}
                groups={boardGroups}
                items={filteredItems}
                members={boardMembers}
                api={api}
                onDataChange={fetchBoardData}
              />
            )}
            
            {currentView === 'calendar' && (
              <ProjectCalendar 
                board={selectedBoard}
                groups={boardGroups}
                items={filteredItems}
                members={boardMembers}
                api={api}
                onDataChange={fetchBoardData}
              />
            )}
            
            {currentView === 'workload' && (
              <ProjectWorkload
                board={selectedBoard}
                groups={boardGroups}
                items={filteredItems}
                members={boardMembers}
                insights={filteredInsights || boardInsights}
                api={api}
                onDataChange={fetchBoardData}
              />
            )}

            {currentView === 'members' && (
              <ModernBoardMembers 
                board={selectedBoard}
                members={boardMembers}
                api={api}
                user={user}
                onDataChange={fetchBoardData}
              />
            )}
            
            {currentView === 'columns' && (
              <ProjectColumns 
                board={selectedBoard}
                api={api}
                onDataChange={handleDataChange}
              />
            )}
            
            {currentView === 'automations' && (
              <ProjectAutomations 
                board={selectedBoard}
                api={api}
                onDataChange={fetchBoardData}
              />
            )}
            
            {currentView === 'tags' && (
              <ProjectTags 
                board={selectedBoard}
                api={api}
                onDataChange={fetchBoardData}
              />
            )}
            
            {currentView === 'analytics' && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                  Analytics Avançados
                </h3>
                <p className="text-gray-600 mb-6">
                  Visualize dados detalhados, tendências e insights do seu projeto
                </p>
                <Button
                  size="lg"
                  onClick={() => setShowAnalytics(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Abrir Relatórios Avançados
                </Button>
              </div>
            )}

            {currentView === 'webhooks' && (
              <BoardWebhooks
                board={selectedBoard}
                api={api}
              />
            )}

            {currentView === 'portfolio' && (
              <PortfolioView
                api={api}
                boards={boards}
              />
            )}

            {currentView === 'goals' && (
              <GoalsOKR
                api={api}
                boards={boards}
              />
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <ModernCreateBoardModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onBoardCreated={handleBoardCreated}
        api={api}
        workspaces={workspaces}
        defaultWorkspaceId={selectedWorkspace?.id || ''}
      />

      <BoardTemplates
        open={showTemplates}
        onOpenChange={setShowTemplates}
        onTemplateSelected={handleTemplateSelected}
        onBoardCreated={handleBoardCreated}
        api={api}
        workspaces={workspaces}
        defaultWorkspaceId={selectedWorkspace?.id || ''}
      />

      {selectedBoard && (
        <>
          <ProjectFilters
            board={selectedBoard}
            api={api}
            items={boardItems}
            insights={boardInsights}
            customColumns={customColumns}
            groups={boardGroups}
            onFiltersApply={handleFiltersApply}
            isOpen={showFilters}
            onClose={() => setShowFilters(false)}
          />
          
          <ProjectGroupCustomization
            board={selectedBoard}
            groups={boardGroups}
            api={api}
            onDataChange={fetchBoardData}
            isOpen={showGroupCustomization}
            onClose={() => setShowGroupCustomization(false)}
          />
          
          <ProjectIntegrations
            board={selectedBoard}
            api={api}
            onDataChange={fetchBoardData}
            isOpen={showIntegrations}
            onClose={() => setShowIntegrations(false)}
          />
          
          <ProjectOptimization
            board={selectedBoard}
            api={api}
            isOpen={showOptimization}
            onClose={() => setShowOptimization(false)}
          />
          
          <ProjectAnalytics
            board={selectedBoard}
            api={api}
            isOpen={showAnalytics}
            onClose={() => setShowAnalytics(false)}
          />
        </>
      )}

      {/* Board Delete Confirmation Modal */}
      {deletingBoardId && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40"
          onClick={() => { setDeletingBoardId(null); setDeletingBoardName(''); }}
        >
          <div
            className="bg-white rounded-xl shadow-2xl p-5 w-80"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-gray-800">Excluir quadro</h3>
            </div>
            <p className="text-xs text-gray-500 mb-1">Tem certeza que deseja excluir o quadro:</p>
            <p className="text-sm font-medium text-gray-800 mb-4 truncate">"{deletingBoardName}"</p>
            <p className="text-xs text-red-500 mb-4">Esta ação é irreversível e removerá todos os dados do quadro.</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setDeletingBoardId(null); setDeletingBoardName(''); }}
                className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteBoard}
                className="text-xs bg-red-500 text-white px-4 py-1.5 rounded-lg hover:bg-red-600 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
